/**
 * portalRequest - the ONE place the portal sends a request to /api with fetch, and the one place
 * the chosen company is kept.
 *
 * v4.30 - 2026-10-15 - ROW 69 (ruling 10): a 409 {"error":"tenant_state_incomplete"} (the company record has no
 *                      workflow state) is its own kind, 'setup_incomplete'; the layout shows a translated
 *                      "setup is incomplete, contact support" message. No customer text lives in this file.
 *
 * v4.28 - 2026-10-13 - G10-B (TEAM-ACCESS-ROLES-01, T2): every request carries X-Tenant-Id = the chosen
 *                      company's tenantUuid. The Axios interceptor in api.ts reads the same choice. A 403
 *                      {"error":"not_a_member"} or 409 {"error":"company_choice_required"} is announced to
 *                      the layout, which clears the choice or shows the choice. No role list, permission
 *                      rule, company id or customer text lives in this file.
 */
import { failClosedClerkSession, getClerkSessionId } from './authGate';
import { PORTAL_VERSION } from '../version';

// Technical storage identifier (not customer text, not a company id).
const CHOICE_STORAGE_KEY = 'portal_company_choice';
const COMPANY_HEADER = 'X-Tenant-Id';
// Technical id of the one-shot message left behind when a chosen company was refused
export const COMPANY_NOTICE_STORAGE_KEY = 'portal_company_notice';

// ---------------------------------------------------------------------------
// Chosen company
// ---------------------------------------------------------------------------
let chosenTenantUuid: string | null = null;
const choiceListeners = new Set<() => void>();

const notifyChoice = () => choiceListeners.forEach((listener) => listener());

export const getChosenCompany = (): string | null => chosenTenantUuid;

export const subscribeCompanyChoice = (listener: () => void): (() => void) => {
  choiceListeners.add(listener);
  return () => { choiceListeners.delete(listener); };
};

const writeStoredChoice = (userId: string | null, tenantUuid: string | null) => {
  try {
    if (!userId || !tenantUuid) {
      localStorage.removeItem(CHOICE_STORAGE_KEY);
    } else {
      localStorage.setItem(CHOICE_STORAGE_KEY, JSON.stringify({ userId, tenantUuid }));
    }
  } catch {
    // Storage can be blocked; the in-memory choice still works for this page load.
  }
};

/** The last choice of THIS signed-in person (a choice stored for another person is ignored). */
export const readStoredChoice = (userId: string | null | undefined): string | null => {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(CHOICE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.userId === userId && typeof parsed.tenantUuid === 'string' ? parsed.tenantUuid : null;
  } catch {
    return null;
  }
};

// Per-company values the pages cache in the tab; they belong to one company and must not survive a change.
const clearCompanyScopedSession = () => {
  try {
    sessionStorage.removeItem('userTenantId');
    sessionStorage.removeItem('userCompanyName');
  } catch { /* optional */ }
};

export const setChosenCompany = (tenantUuid: string | null, userId: string | null): void => {
  const changed = chosenTenantUuid !== tenantUuid;
  if (changed) clearCompanyScopedSession();
  chosenTenantUuid = tenantUuid || null;
  writeStoredChoice(userId, chosenTenantUuid);
  if (changed) notifyChoice();
};

export const clearChosenCompany = (): void => {
  const changed = chosenTenantUuid !== null;
  if (changed) clearCompanyScopedSession();
  chosenTenantUuid = null;
  writeStoredChoice(null, null);
  if (changed) notifyChoice();
};

/** Load the stored choice of this person into memory (before the first request of the page). */
export const restoreChosenCompany = (userId: string | null | undefined): string | null => {
  const stored = readStoredChoice(userId);
  if (stored && stored !== chosenTenantUuid) {
    chosenTenantUuid = stored;
    notifyChoice();
  }
  return chosenTenantUuid;
};

// ---------------------------------------------------------------------------
// Client decisions are logged with the company id, never an email
// ---------------------------------------------------------------------------
type DecisionSink = (event: string, fields: Record<string, unknown>) => void;
let decisionSink: DecisionSink | null = null;

export const setDecisionSink = (sink: DecisionSink | null): void => {
  decisionSink = sink;
};

const looksLikeEmail = (value: unknown): boolean => typeof value === 'string' && value.includes('@');

export const logClientDecision = (event: string, fields: Record<string, unknown> = {}): void => {
  const safe: Record<string, unknown> = {};
  Object.keys(fields).forEach((key) => {
    if (/e-?mail/i.test(key) || looksLikeEmail(fields[key])) return; // defensive: no email in any line
    safe[key] = fields[key];
  });
  const line = Object.keys(safe).map((key) => `${key}=${String(safe[key])}`).join(' ');
  // eslint-disable-next-line no-console
  console.info(`[portal-decision] portal=${PORTAL_VERSION} event=${event} ${line}`.trim());
  if (decisionSink) {
    try { decisionSink(event, { ...safe, portalVersion: PORTAL_VERSION }); } catch { /* telemetry never blocks the UI */ }
  }
};

// ---------------------------------------------------------------------------
// Company access refusals (403 not_a_member, 409 company_choice_required)
// ---------------------------------------------------------------------------
export type CompanyAccessKind = 'not_a_member' | 'company_choice_required' | 'unavailable' | 'setup_incomplete'; // v4.30 - ROW 69

// The server's 503 answers for the company, role, companies and pending-invite lookups ({"error": <code>}).
// A 503 with one of these codes is a "try again" for the person, never a refusal of access.
const UNAVAILABLE_CODES = [
  'membership_lookup_unavailable',
  'role_lookup_unavailable',
  'companies_lookup_unavailable',
  'invite_lookup_unavailable',
];
type CompanyAccessHandler = (kind: CompanyAccessKind) => void;
const accessHandlers = new Set<CompanyAccessHandler>();

export const onCompanyAccess = (handler: CompanyAccessHandler): (() => void) => {
  accessHandlers.add(handler);
  return () => { accessHandlers.delete(handler); };
};

export const classifyCompanyAccess = (status: number, body: unknown): CompanyAccessKind | null => {
  const code = body && typeof body === 'object' ? (body as { error?: unknown }).error : null;
  if (status === 403 && code === 'not_a_member') return 'not_a_member';
  if (status === 409 && code === 'company_choice_required') return 'company_choice_required';
  if (status === 409 && code === 'tenant_state_incomplete') return 'setup_incomplete'; // v4.30 - ROW 69 (ruling 10)
  if (status === 503 && typeof code === 'string' && UNAVAILABLE_CODES.includes(code)) return 'unavailable';
  return null;
};

export const reportCompanyAccess = (kind: CompanyAccessKind, route: string): void => {
  logClientDecision(kind, { company: getChosenCompany() || 'none', route });
  accessHandlers.forEach((handler) => handler(kind));
};

// ---------------------------------------------------------------------------
// The request wrapper
// ---------------------------------------------------------------------------
const toPlainHeaders = (headers: HeadersInit | undefined): Record<string, string> => {
  const out: Record<string, string> = {};
  if (!headers) return out;
  if (Array.isArray(headers)) {
    headers.forEach(([key, value]) => { out[key] = value; });
  } else if (typeof (headers as Headers).forEach === 'function' && typeof Headers !== 'undefined' && headers instanceof Headers) {
    headers.forEach((value, key) => { out[key] = value; });
  } else {
    Object.assign(out, headers as Record<string, string>);
  }
  return out;
};

export interface PortalRequestOptions {
  /**
   * true (default): a 401 (or a 404 on /api/me) fails the Clerk session closed.
   * false: the caller handles the status itself (public pages, sign-in checks).
   */
  gate?: boolean;
}

export const portalRequest = async (
  input: RequestInfo | URL,
  init?: RequestInit,
  options: PortalRequestOptions = {},
): Promise<Response> => {
  const headers = toPlainHeaders(init?.headers);
  const chosen = getChosenCompany();
  const hasCompanyHeader = Object.keys(headers).some((key) => key.toLowerCase() === COMPANY_HEADER.toLowerCase());
  if (chosen && !hasCompanyHeader) headers[COMPANY_HEADER] = chosen;

  const sessionAtRequest = getClerkSessionId();
  const response = await fetch(input, { ...init, headers });

  if (options.gate !== false &&
      (response.status === 401 || (response.status === 404 && String(input) === '/api/me'))) {
    await failClosedClerkSession(undefined, undefined, sessionAtRequest);
  }

  if (response.status === 403 || response.status === 409 || response.status === 503) {
    try {
      const body = await response.clone().json();
      const kind = classifyCompanyAccess(response.status, body);
      if (kind) reportCompanyAccess(kind, String(input));
    } catch {
      // not a JSON refusal; nothing to announce
    }
  }
  return response;
};

/** For pages that handle the status themselves (public pages, sign-in checks): same header, no sign-out gate. */
export const portalRequestUngated = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> =>
  portalRequest(input, init, { gate: false });

export const COMPANY_REQUEST_HEADER = COMPANY_HEADER;
